import {
  ObjectConfig,
  ObjectConfigAction,
  ObjectConfigStatus,
  ObjectKeys,
  RawObject,
  RawObjectKeys
} from '../interfaces/object';
import {
  ObjectsHeaderColumn,
  ObjectsHeaderColumnRawData,
  ObjectsHeadersRawData,
  ValidObjectsHeaders
} from '../interfaces/object-header';
import { EntityRawAction } from '../../../entity-actions/interfaces/entity-actions';
import { EntityActionsFactory } from '../../../entity-actions/factory/entityActionsFactory';
import { OBJECT_CONFIG_ACTIONS_DISPLAY_DETAILS } from '../constants/object-config-actions';

export function ObjectsHeadersFactory(configHeaders: ObjectsHeadersRawData): ValidObjectsHeaders {
  const {
    load_mode: loadMode,
    keys,
    field_count: fieldCount,
    ...dynamicColumns
  } = configHeaders.data.column_configs;

  const data: ValidObjectsHeaders = {
    ...(loadMode ? { loadMode: ColumnFactory(loadMode) } : {}),
    ...(keys ? { keys: ColumnFactory(keys) } : {}),
    ...(fieldCount ? { fieldCount: ColumnFactory(fieldCount) } : {})
  };

  Object.entries(dynamicColumns).forEach(([key, value]) => {
    data[key] = ColumnFactory(value);
  });

  return sortObjectsHeadersByOrder(data);
}

function ColumnFactory(rawColumn: ObjectsHeaderColumnRawData): ObjectsHeaderColumn {
  return {
    displayName: rawColumn.display_name,
    description: rawColumn.description,
    order: rawColumn.order,
    ...(rawColumn?.additional_config ? { additionalConfig: rawColumn?.additional_config } : null)
  };
}

export function ObjectsFactory(rawObjects: RawObject[]): ObjectConfig[] {
  return rawObjects?.map(rawObject => {
    return ObjectFactory(rawObject);
  });
}

export function ObjectFactory(rawObject: RawObject) {
  const {
    id,
    keys,
    field_count: fieldCount,
    status,
    reason: statusReason,
    load_mode: loadMode,
    is_editable: isEditable,
    actions,
    ...dynamicFields
  } = rawObject;

  const objectConfigStatus = status as ObjectConfigStatus;

  const objectRowData: ObjectConfig = {
    id,
    fieldCount,
    loadMode,
    status: objectConfigStatus,
    statusReason,
    isEditable,
    actions: ObjectActionsFactory(actions || []),
    keys: ObjectKeysFactory(keys),
    isConfigurable: ![ObjectConfigStatus.INACCESSIBLE, ObjectConfigStatus.INCONSISTENT].includes(
      objectConfigStatus
    ),
    isEditConfigAllowed: ![ObjectConfigStatus.INACCESSIBLE].includes(objectConfigStatus),
    ...dynamicFields
  };

  return objectRowData;
}

export function ObjectActionsFactory(rawActions: EntityRawAction<ObjectConfigAction>[]) {
  return EntityActionsFactory(rawActions, OBJECT_CONFIG_ACTIONS_DISPLAY_DETAILS);
}

function ObjectKeysFactory(keys: RawObjectKeys): ObjectKeys {
  const { required_keys: requiredKeys, ...remaining } = keys;

  return {
    requiredKeys,
    ...remaining
  };
}

function sortObjectsHeadersByOrder(headers: ValidObjectsHeaders) {
  const sortedKeys = Object.keys(headers).sort((a, b) => headers[a]!.order - headers[b]!.order);

  return sortedKeys.reduce((acc, key) => {
    return {
      ...acc,
      [key]: headers[key]
    };
  }, {}) as ValidObjectsHeaders;
}
